import { useMemo } from "react";
import { Users, MessageSquare } from "lucide-react";
import { formatWaiting, formatReservationTime } from "@/utils/time";
import useAppStore from "@/store/useAppStore";

/**
 * CR-0012 — unified 11px typography scale; action bar fixed height;
 * time string always visible above the bar.
 *
 * Phase G (Δ-11) — subscribes to `useAppStore.tick` so the waiting
 * string re-derives every second. Per C-17, only the `tick` selector
 * and the `table` prop drive renders; no full `tables` map subscription.
 */
export default function ReservedCard({ table }) {
  const tick = useAppStore((s) => s.tick);

  const guestCount = (table.adult || 0) + (table.children || 0);
  const waitingDisplay = useMemo(
    () => formatWaiting(table.waiting_start_time),
    // tick intentionally in deps: forces recompute every second.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table.waiting_start_time, tick]
  );
  const locationLabel =
    table.location_type === "indoor"
      ? "IN"
      : table.location_type === "outdoor"
      ? "OUT"
      : null;
  const hasComment = !!table.comments?.trim();

  return (
    <div
      data-testid={`reserved-card-${table.code}`}
      className="flex flex-col h-full px-2.5 py-1.5 overflow-hidden"
    >
      {/* Row 1: people count + reservation time — uniform 11px */}
      <div className="flex items-start justify-between min-w-0">
        <div className="flex items-center gap-1 min-w-0">
          <Users size={11} style={{ color: "#424242" }} />
          <span
            className="text-[11px] font-semibold truncate leading-none"
            style={{ color: "#424242" }}
          >
            {guestCount}
          </span>
        </div>
        <span
          className="text-[11px] truncate ml-1 leading-none"
          style={{ color: "#424242" }}
        >
          {formatReservationTime(table.reservation_time) || ""}
        </span>
      </div>

      {/* Row 2: table_number + comment icon */}
      <div className="flex items-center justify-between min-w-0 mt-1 min-h-[12px]">
        <span
          className="text-[11px] truncate leading-none"
          style={{ color: "#424242" }}
        >
          {table.table_number || ""}
        </span>
        {hasComment && (
          <MessageSquare
            data-testid={`comment-icon-${table.code}`}
            size={11}
            style={{ color: "#6B7280" }}
          />
        )}
      </div>

      {/* Location */}
      {locationLabel && (
        <div className="text-center mt-1">
          <span
            className="text-[11px] font-medium leading-none"
            style={{ color: "#424242" }}
          >
            {locationLabel}
          </span>
        </div>
      )}

      {/* Code badge — centered, 11px per Q-A */}
      <div className="flex items-center justify-center my-auto min-h-0">
        <div
          data-testid={`code-badge-${table.code}`}
          className="px-2.5 py-0.5 rounded-full max-w-full"
          style={{ backgroundColor: "#D0D0D0" }}
        >
          <span
            className="text-[11px] font-bold truncate block leading-tight"
            style={{ color: "#424242" }}
          >
            {table.code}
          </span>
        </div>
      </div>

      {/* Waiting time string — always shown per Q-B; live via Phase G tick */}
      <div className="text-center mt-1 truncate">
        <span
          data-testid={`waiting-time-${table.code}`}
          className="text-[11px] font-medium leading-none"
          style={{ color: "#424242" }}
        >
          {waitingDisplay || ""}
        </span>
      </div>

      {/* Waiting bar — fixed height; never shrinks */}
      <div
        data-testid={`waiting-bar-${table.code}`}
        className="mt-1 flex items-center justify-center rounded-md py-1 shrink-0"
        style={{ backgroundColor: "#000000" }}
      >
        <span className="text-[11px] font-bold text-white leading-none">
          Waiting
        </span>
      </div>
    </div>
  );
}
